import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _toConsumableArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
var _excluded = ["children", "isActive", "isHighlighted", "title"];
/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import cx from 'classnames';
import React, { forwardRef, useRef, useState, useEffect } from 'react';
import { usePrefix } from '../../internal/usePrefix.js';
import { useMergedRefs } from '../../internal/useMergedRefs.js';

/**
 * Determines if the content of an element is truncated.
 *
 * Merges a forwarded ref with a local ref to check the element's dimensions.
 *
 * @template T
 * @param forwardedRef - A ref passed from the parent component.
 * @param deps - Dependencies to re-run the truncation check.
 * @returns An object containing the truncation state and the merged ref.
 */
var useIsTruncated = function useIsTruncated(forwardedRef) {
  var deps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var localRef = useRef(null);
  var mergedRef = useMergedRefs([].concat(_toConsumableArray(forwardedRef ? [forwardedRef] : []), [localRef]));
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isTruncated = _useState2[0],
    setIsTruncated = _useState2[1];
  useEffect(function () {
    var element = localRef.current;
    if (element) {
      var offsetWidth = element.offsetWidth,
        scrollWidth = element.scrollWidth;
      setIsTruncated(offsetWidth < scrollWidth);
    }
  }, [localRef].concat(_toConsumableArray(deps)));
  return {
    isTruncated: isTruncated,
    ref: mergedRef
  };
};
/**
 * `ListBoxMenuItem` is a helper component for managing the container class
 * name, alongside any classes for any corresponding states, for a generic list
 * box menu item.
 */
var ListBoxMenuItem = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var _cx;
  var children = _ref.children,
    _ref$isActive = _ref.isActive,
    isActive = _ref$isActive === void 0 ? false : _ref$isActive,
    _ref$isHighlighted = _ref.isHighlighted,
    isHighlighted = _ref$isHighlighted === void 0 ? false : _ref$isHighlighted,
    title = _ref.title,
    rest = _objectWithoutProperties(_ref, _excluded);
  var prefix = usePrefix();
  var menuItemOptionRefProp = forwardedRef && typeof forwardedRef !== 'function' ? forwardedRef.menuItemOptionRef : undefined;
  var _useIsTruncated = useIsTruncated(menuItemOptionRefProp, [children]),
    isTruncated = _useIsTruncated.isTruncated,
    menuItemOptionRef = _useIsTruncated.ref;
  var className = cx("".concat(prefix, "--list-box__menu-item"), (_cx = {}, _defineProperty(_cx, "".concat(prefix, "--list-box__menu-item--active"), isActive), _defineProperty(_cx, "".concat(prefix, "--list-box__menu-item--highlighted"), isHighlighted), _cx));
  return /*#__PURE__*/React.createElement("li", _extends({}, rest, {
    className: className,
    title: isTruncated ? title : undefined
  }), /*#__PURE__*/React.createElement("div", {
    className: "".concat(prefix, "--list-box__menu-item__option"),
    ref: menuItemOptionRef
  }, children));
});
ListBoxMenuItem.displayName = 'ListBoxMenuItem';
export { ListBoxMenuItem as default };